/**
 * components/inspection/FormalReqCreateModal.js
 * АЛБАН ШААРДЛАГА / АКТ ҮҮСГЭХ — бөглөх маягт.
 *
 * ⚠ ЯАГААД МАЯГТ БОЛОВ (2026-10-05, хэрэглэгчийн шаардлага):
 *   «үүсгэх дарах үед шууд бөглөх талбаруудыг бөглөх хэрэгтэй, хаана,
 *    ямар цех дээр шалгалт хийж байгаа нь шууд бөглөгдөх»,
 *   «БН-ийн эсвэл хэсэг сонгуулж удирдлага сонголт оруулах»,
 *   «шалгуулсан хэсгийн дарга сонголт хийх»,
 *   «дүрэм журмын дагуу юу юу бөглөх, хавсаргах».
 *   Урьд нь зөвхөн `Modal.confirm` («Үүсгэх үү?») байсан тул баримт
 *   хаяггүй, толгой догол мөргүй, хугацаагүй үүсч, ХАБЭАХ дараа нь
 *   албан шаардлагын хуудаснаас бүгдийг гараар нөхдөг байв.
 *
 * ⚠ ЖУРМЫН ЗААВАР нь ЛАВЛАХААС (`/xab/formal-req-rules`) — кодод
 *   хатуу бичихгүй. Шатны дагуу «хэнд хаяглагдах», «юу бичих», «юу
 *   хавсаргах», «биелэлтэд хяналт тавих» нь заалтын дугаартайгаа
 *   гарч, дарвал журмын бүтэн текст нээгдэнэ.
 *
 * ⚠ ХООСОН ТАЛБАР бол ХОРИГЛОХГҮЙ, АНХААРУУЛНА. Журмын заавал
 *   шаардахыг нь (хаяглалт) зөвхөн сануулна — ХАБЭАХ нь онцгой
 *   тохиолдолд хаягийг дараа бөглөх шаардлага гардаг
 *   ([[feedback_no_ai_invented_limits]]).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  DatePicker,
  Descriptions,
  Drawer,
  Divider,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  message,
} from "antd";
import moment from "moment";

import DepartmentTseh from "src/components/departmentTseh";
import DepartmentNegj from "src/components/departmentNegj";
import { xabFormalReqApi, xabInspectionApi } from "src/api/xabApi";
import { xabFormalReqRuleApi } from "src/api/xabApi";

const MUTED = "#8A94A6";
const INK = "#1F2430";

/** Журмын нэг заавар — эшлэлтэй, дарвал заалт нээгдэнэ. */
function Guide({ label, part, onClause }) {
  if (!part) return null;
  return (
    <div style={{ marginBottom: 7 }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: MUTED }}>
        {label}
      </div>
      {part.given ? (
        <div style={{ fontSize: 12, color: INK, lineHeight: 1.35 }}>
          {part.text}
          {part.clause ? (
            <Tag
              color={part.clause_text ? "blue" : "orange"}
              style={{ marginLeft: 6, cursor: "pointer", fontSize: 10 }}
              onClick={() => onClause?.(part)}
            >
              <i className="fas fa-book" style={{ marginRight: 3 }} />
              {part.reg_code || "журам"} · {part.clause}
            </Tag>
          ) : null}
        </div>
      ) : (
        <Tooltip title="Журамд тусгайлан заагаагүй — ХАБЭА хэлтэс шийднэ">
          <Tag style={{ fontSize: 10, color: MUTED }}>журамд заагаагүй</Tag>
        </Tooltip>
      )}
    </div>
  );
}

export default function FormalReqCreateModal({
  open,
  onClose,
  insp,
  findingCount,
  onCreated,
}) {
  const [form] = Form.useForm();
  const [rule, setRule] = useState(null);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [clause, setClause] = useState(null);

  /* ХАЯГЛАЛТ: нэгж → удирдлага. ⚠ Анхдагчаар ШАЛГУУЛСАН нэгжээр
     (шалгалтын цех/хэсэг) — хэрэглэгч «ямар цех дээр шалгалт хийж
     байгаа нь шууд бөглөгдөх» гэж шаардсан. */
  const [aTseh, setATseh] = useState(null);
  const [aBn, setABn] = useState(null);
  /* ⚠ ХҮЛЭЭН АВАГЧИЙН ТӨРӨЛ (2026-10-05, хэрэглэгч: «хэрэв дотоод
     нэгж бол цех сонголт, гадны байгууллага [бол тэр]»). Албан
     шаардлага нь дотоод нэгжийн дарга (ХЭМАБМТ-06 4.1.47) руу ч,
     ГАДНЫ гүйцэтгэгч/цэцэрлэг/сургууль руу ч явдаг —
     `formal_requirement.recipient_org`-ийн зориулалт яг тэр.
     ⚠ Хоёуланг ЗЭРЭГ бичихгүй: бланкны «…-д» хэсэгт хоёр эзэн гарвал
     хэнээс шаардаж байгаа нь мэдэгдэхгүй болно (backend ч нэгийг л
     авна). Тиймээс сонголт нь РАДИО, хоёр талбар биш. */
  const [rcpKind, setRcpKind] = useState("internal");
  const [orgs, setOrgs] = useState([]);
  const [tsehId, setTsehId] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [wLoading, setWLoading] = useState(false);
  const wTimer = useRef(null);

  const stageCode = insp?.stage_code;

  /* ── Лавлахаас шатны дүрэм ба цуврал ── */
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      xabFormalReqRuleApi.rules().catch(() => null),
      xabFormalReqRuleApi.series().catch(() => null),
    ])
      .then(([r, s]) => {
        const list = r?.items || [];
        setRule(list.find((x) => x.stage_code === stageCode) || null);
        setSeries(s?.items || []);
      })
      .finally(() => setLoading(false));
  }, [open, stageCode]);

  /* ── Маягтыг ШАЛГАЛТААС урьдчилан бөглөнө ── */
  useEffect(() => {
    if (!open || !insp) return;
    setATseh(insp.tseh_code || null);
    setABn(insp.bn_code || null);
    setRcpKind("internal");
    form.setFieldsValue({
      title:
        `${insp.stage_name || "Хяналт шалгалт"} — ` +
        `${insp.tseh_name || insp.bn_name || ""}`.trim(),
      // ⚠ Хугацаа: журамд тодорхой хоног заагаагүй тул 14 хоног гэж
      //   САНАЛ болгоно (backend-ийн DEFAULT_DUE_DAYS-тай нийцүүлэв).
      //   ХАБЭАХ өөрөө солино.
      due_date: moment().add(14, "days"),
      report_day: 25,
      recipient_tn: undefined,
      summary: undefined,
    });
  }, [open, insp, form]);

  /* ── «Иймд шаардах нь» — лавлахын загвараас орлуулга ──
     ⚠ Загварыг КЛИЕНТ дээр ч орлуулна: хэрэглэгч үүсгэхээс ӨМНӨ
       уншиж, засах боломжтой байх ёстой. Backend ч ижил логикоор
       нөхдөг (хоосон илгээвэл) — хоёр тал ижил эх сурвалжтай. */
  const introPreview = useMemo(() => {
    const t = rule?.intro_template;
    if (!t || !insp) return "";
    const map = {
      tseh: insp.tseh_name || insp.tseh_code || "",
      bn: insp.bn_name || insp.bn_code || "",
      date: insp.inspection_date
        ? moment(insp.inspection_date).format("YYYY-MM-DD")
        : moment().format("YYYY-MM-DD"),
      count: findingCount || 0,
      stage: insp.stage_name || "хяналт шалгалт",
      location: insp.area_location || "",
      code: insp.code || "",
    };
    return t.replace(/\{(\w+)\}/g, (m, k) =>
      map[k] !== undefined ? String(map[k]) : m,
    );
  }, [rule, insp, findingCount]);

  useEffect(() => {
    if (!open) return;
    // Хэрэглэгч засаагүй байвал л загвараар дарна
    const cur = form.getFieldValue("summary");
    if (!cur && introPreview) form.setFieldsValue({ summary: introPreview });
  }, [open, introPreview, form]);

  /* ── ГАДНЫ байгууллагууд — ЗӨВХӨН хэрэгтэй үед татна ──
     ⚠ Эх сурвалж нь ХСБ-ийн байгууллагын лавлах (`product.Company`):
       гүйцэтгэгч, нийлүүлэгч, цэцэрлэг зэрэг аль хэдийн бүртгэлтэй.
       ШИНЭ лавлах зохиохгүй — хоёр жагсаалт зөрвөл албан бичгийн
       хаяг нь аль нь үнэн болох нь мэдэгдэхгүй болно. */
  useEffect(() => {
    if (!open || rcpKind !== "external" || orgs.length) return;
    xabFormalReqRuleApi
      .companies()
      .then((d) => setOrgs(d?.items || []))
      .catch(() => setOrgs([]));
  }, [open, rcpKind, orgs.length]);

  /* ── Удирдлага хайх — сонгосон нэгжийн хүрээнд ── */
  const searchHeads = useCallback(
    (q) => {
      clearTimeout(wTimer.current);
      wTimer.current = setTimeout(async () => {
        setWLoading(true);
        try {
          const d = await xabInspectionApi.searchWorkers({
            q: q || undefined,
            /* ⚠ ХАМРАХ ХҮРЭЭ нь сонгосон нэгж — «шалгуулсан хэсгийн
               дарга»-г олоход бүх үйлдвэрээс хайвал ижил овогтой
               хүмүүс холилдоно. */
            tseh_code: aTseh || undefined,
            bn_code: aBn || undefined,
            /* ⚠ `mgmt=1` ЗААВАЛ (2026-10-05, хэрэглэгч: «цехийн
               удирдлага олдохгүй бна»). Үүнгүй бол цех 25 дээр 200
               ажилтан (Дээж авагч, монтёр…) үсгийн дараалалаар ирж,
               цехийн дарга 50-ийн лимитэд БАГТАХГҮЙ байв. Хэмжилт:
               tseh=25 → 200 хүн · mgmt=1 → 114 · хэсэгтэй → 23.
               Журмын хаяглалт нь «нэгжийн ДАРГА» тул удирдах
               түвшнээр шүүх нь зөв (worker_lookup-ийн MGMT_RATE_IDS
               нь `rate_id` 1-2 = удирдах ажилтан + ИТА). */
            mgmt: 1,
            /* ⚠ Лимит ӨНДӨР: сервер нь нэрийн дарааллаар эрэмбэлдэг
               тул бага лимит нь «О»-гоос хойшхи даргыг таслана. */
            limit: 200,
          });
          setWorkers(d?.workers || []);
        } catch {
          setWorkers([]);
        } finally {
          setWLoading(false);
        }
      }, 300);
    },
    [aTseh, aBn],
  );

  useEffect(() => {
    if (open) searchHeads("");
  }, [open, searchHeads]);

  const seriesRow = useMemo(
    () => series.find((s) => s.code === (rule?.series_code || "")),
    [series, rule],
  );

  const submit = async () => {
    const v = await form.validateFields().catch((e) => e);
    if (v?.errorFields) return;
    /* ⚠ ХАЯГЛАЛТГҮЙГ ХОРИГЛОХГҮЙ, АСУУНА: журамд хаяглалтыг зарим
       шатанд заагаагүй, мөн ХАБЭАХ хаягийг дараа бөглөх тохиолдол
       гардаг. Гэхдээ чимээгүй өнгөрөөвөл хаяггүй албан бичиг
       хэвлэгдэнэ. */
    const noRcp =
      rcpKind === "internal" ? !v.recipient_tn : !(v.recipient_org || "").trim();
    if (noRcp) {
      const go = await new Promise((res) =>
        Modal.confirm({
          title: "Хэнд хаяглахыг заагаагүй",
          content:
            "Хаяглалтгүй бол хэвлэсэн бланкны дээд талын «…-д» хэсэг " +
            "хоосон гарна. Дараа нь албан шаардлагын хуудаснаас " +
            "нөхөж болно. Үргэлжлүүлэх үү?",
          okText: "Үргэлжлүүлэх",
          cancelText: "Буцаад заах",
          onOk: () => res(true),
          onCancel: () => res(false),
        }),
      );
      if (!go) return;
    }
    setBusy(true);
    try {
      const res = await xabFormalReqApi.fromInspection(insp.id, {
        title: (v.title || "").trim() || undefined,
        summary: (v.summary || "").trim() || undefined,
        due_date: v.due_date ? v.due_date.format("YYYY-MM-DD") : undefined,
        report_day: v.report_day || 25,
        /* ⚠ Backend нь `recipient_org` ирвэл ТҮҮНИЙГ л авч,
           `recipient_tn`-ийг NULL болгоно — хоёр хаяг зэрэг
           бичигдэхээс сэргийлсэн. Тиймээс энд ч аль нэгийг л явуулна. */
        ...(rcpKind === "external"
          ? {
              recipient_org: (v.recipient_org || "").trim() || undefined,
              recipient_name: (v.recipient_name || "").trim() || undefined,
              recipient_position:
                (v.recipient_position || "").trim() || undefined,
            }
          : { recipient_tn: v.recipient_tn || undefined }),
        series_code: v.series_code || undefined,
      });
      onCreated?.(res);
      onClose?.();
    } catch (e) {
      message.error(
        e?.response?.data?.message || "Албан шаардлага үүсгэж чадсангүй",
      );
    } finally {
      setBusy(false);
    }
  };

  const docLabel =
    stageCode === "irregular" || stageCode === "monitoring"
      ? "АКТ-АЛБАН ШААРДЛАГА"
      : "АЛБАН ШААРДЛАГА";

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        width={720}
        title={
          <div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>
              {docLabel} үүсгэх
            </div>
            <div style={{ fontSize: 11.5, color: MUTED, fontWeight: 400 }}>
              {insp?.code} · {insp?.stage_name}
            </div>
          </div>
        }
        extra={
          <Space>
            <Button onClick={onClose}>Болих</Button>
            <Button type="primary" loading={busy} onClick={submit}>
              Үүсгэх
            </Button>
          </Space>
        }
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: 30 }}>
            <Spin />
          </div>
        ) : (
          <>
            {/* ── ШАЛГАЛТААС АВТОМАТААР ──────────────────────────── */}
            <Descriptions
              size="small"
              column={1}
              bordered
              labelStyle={{ width: 150, fontSize: 11.5 }}
              contentStyle={{ fontSize: 12 }}
              style={{ marginBottom: 12 }}
            >
              <Descriptions.Item label="Цех / хэлтэс">
                {insp?.tseh_name || insp?.tseh_code || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Хэсэг">
                {insp?.bn_name || insp?.bn_code || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Хаана (ажлын байр)">
                {insp?.area_location || (
                  <span style={{ color: MUTED }}>заагаагүй</span>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Шалгалтын огноо">
                {insp?.inspection_date
                  ? moment(insp.inspection_date).format("YYYY-MM-DD")
                  : "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Шалгагч">
                {insp?.inspector_name || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Зөрчлийн тоо">
                <b>{findingCount || 0}</b> зөрчил мөр болж хуулагдана
              </Descriptions.Item>
              <Descriptions.Item label="Албан дугаар">
                {seriesRow?.next_sample ? (
                  <Space size={6}>
                    <Tag color="volcano" style={{ fontWeight: 700 }}>
                      {seriesRow.next_sample}
                    </Tag>
                    <span style={{ fontSize: 10.5, color: MUTED }}>
                      {seriesRow.name}
                    </span>
                  </Space>
                ) : (
                  <Tooltip title="Шатны хяналтын албан шаардлага нь дотоод баримт тул албан бичгийн дэвтэрт дугаарлагдахгүй">
                    <Tag>дугаарлахгүй</Tag>
                  </Tooltip>
                )}
              </Descriptions.Item>
            </Descriptions>

            {/* ── ЖУРМЫН ЗААВАР ──────────────────────────────────── */}
            {rule ? (
              <div
                style={{
                  border: "1px solid #D7E0EA",
                  background: "#F7FAFD",
                  borderRadius: 8,
                  padding: 10,
                  marginBottom: 12,
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#1F4E79",
                    marginBottom: 7,
                  }}
                >
                  <i className="fas fa-scale-balanced"
                     style={{ marginRight: 5 }} />
                  ЖУРМЫН ШААРДЛАГА — {insp?.stage_name}
                </div>
                <Guide label="ХЭНД ХАЯГЛАГДАХ" part={rule.addressee}
                       onClause={setClause} />
                <Guide label="ЮУ БИЧИХ" part={rule.content}
                       onClause={setClause} />
                <Guide label="ЮУ ХАВСАРГАХ" part={rule.attach}
                       onClause={setClause} />
                <Guide label="ХҮРГЭХ АРГА" part={rule.delivery}
                       onClause={setClause} />
                <Guide label="БИЕЛЭЛТЭД ХЯНАЛТ ТАВИХ" part={rule.monitor}
                       onClause={setClause} />
                {rule.note ? (
                  <div style={{ fontSize: 10.5, color: "#B45309", marginTop: 4 }}>
                    <i className="fas fa-circle-info"
                       style={{ marginRight: 4 }} />
                    {rule.note}
                  </div>
                ) : null}
              </div>
            ) : (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message="Энэ шатны журмын дүрэм лавлахад алга"
                description="/settings/reference → «Албан шаардлага» дээр бөглөнө. Үүсгэхэд саад болохгүй."
              />
            )}

            <Form form={form} layout="vertical" size="small">
              <Form.Item
                name="title"
                label="Баримтын гарчиг"
                rules={[{ required: true, message: "Гарчиг бичнэ үү" }]}
              >
                <Input maxLength={500} />
              </Form.Item>

              <Divider style={{ margin: "6px 0 10px" }}>
                <span style={{ fontSize: 11, color: MUTED }}>
                  ХҮЛЭЭН АВАГЧ, АЛБАН ДУГААР
                </span>
              </Divider>

              {/* ⚠ ХОЁР ТӨРӨЛ (2026-10-05). Албан шаардлага нь дотоод
                  нэгжийн дарга руу ч (ХЭМАБМТ-06 4.1.47), ГАДНЫ
                  гүйцэтгэгч/цэцэрлэг/сургууль руу ч явдаг. Радио
                  болгосон нь зориуд: хоёр хаяг зэрэг бичигдвэл
                  бланкны «…-д» хэсэгт хоёр эзэн гарч, хэнээс шаардаж
                  байгаа нь мэдэгдэхгүй болно. */}
              <Radio.Group
                value={rcpKind}
                size="small"
                buttonStyle="solid"
                style={{ marginBottom: 10 }}
                onChange={(e) => {
                  setRcpKind(e.target.value);
                  form.setFieldsValue({
                    recipient_tn: undefined,
                    recipient_org: undefined,
                    recipient_name: undefined,
                    recipient_position: undefined,
                  });
                }}
              >
                <Radio.Button value="internal">
                  <i className="fas fa-industry" style={{ marginRight: 5 }} />
                  Дотоод нэгж
                </Radio.Button>
                <Radio.Button value="external">
                  <i className="fas fa-building" style={{ marginRight: 5 }} />
                  Гадны байгууллага
                </Radio.Button>
              </Radio.Group>

              {rcpKind === "internal" ? (
                <>
                  {/* ⚠ Нэгжийг ШАЛГАЛТААС урьдчилан тавина — ихэнхдээ
                      шалгуулсан нэгжийн дарга хүлээн авна. Гэхдээ
                      СОЛИХ боломжтой: зорилтот хяналтад шалгалтыг
                      ЯВУУЛСАН нэгж хүлээн авдаг (ХЭМАБМТ-15 4.11). */}
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 11.5, marginBottom: 3 }}>
                      Цех / хэлтэс
                    </div>
                    <DepartmentTseh
                      noAutoSelect
                      value={aTseh || undefined}
                      onChange={(code, id) => {
                        setATseh(code || null);
                        setTsehId(id || null);
                        setABn(null);
                        form.setFieldsValue({ recipient_tn: undefined });
                      }}
                    />
                  </div>

                  {/* ⚠ ХЭСЭГ нь хамрах хүрээг НАРИЙСГАНА. Хэмжилт
                      (2026-10-05): цех 25 дээр удирдах түвшин 114 хүн,
                      хэсэг заавал 23 болж, «хэсгийн дарга» нүдэнд
                      шууд өртөнө. Хоосон бол бүх цехээр хайна. */}
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 11.5, marginBottom: 3 }}>
                      Хэсэг{" "}
                      <span style={{ color: MUTED, fontSize: 10.5 }}>
                        (хоосон = бүх цехээр хайна)
                      </span>
                    </div>
                    <DepartmentNegj
                      paranetid={tsehId || undefined}
                      parentCode={aTseh || undefined}
                      value={aBn || undefined}
                      onChange={(code) => {
                        setABn(code || null);
                        form.setFieldsValue({ recipient_tn: undefined });
                      }}
                    />
                  </div>

                  <Form.Item
                    name="recipient_tn"
                    label="Хүлээн авах удирдлага (шалгуулсан нэгжийн дарга)"
                    extra={
                      rule?.addressee?.given
                        ? `Журмаар: ${rule.addressee.text}`
                        : "Журамд заагаагүй — ХАБЭА хэлтэс шийднэ. Зөвхөн удирдах түвшний ажилтан гарна."
                    }
                  >
                    <Select
                      allowClear
                      showSearch
                      filterOption={false}
                      loading={wLoading}
                      placeholder="Нэр эсвэл ТН-ээр хайх"
                      onSearch={searchHeads}
                      notFoundContent={
                        wLoading ? (
                          <Spin size="small" />
                        ) : (
                          "Олдсонгүй — хэсгээ солих эсвэл нэрээр хайна уу"
                        )
                      }
                      options={workers.map((w) => ({
                        value: w.tn,
                        label: `${w.name || "?"}${
                          w.position ? ` · ${w.position}` : ""
                        } (${w.tn})`,
                      }))}
                    />
                  </Form.Item>
                </>
              ) : (
                <>
                  <Form.Item
                    name="recipient_org"
                    label="Гадны байгууллага"
                    extra="ХСБ-ийн байгууллагын лавлахаас. Байхгүй бол бичиж оруулж болно."
                    rules={[
                      { required: true, message: "Байгууллагыг заана уу" },
                    ]}
                  >
                    {/* ⚠ `mode` эсвэл `tags` БИШ: нэг л байгууллага.
                        Гэхдээ лавлахад байхгүй байгууллага гарч ирэх
                        тул чөлөөт бичилтийг зөвшөөрөв (`AutoComplete`
                        шиг — Select-ийн `showSearch` + бичсэнээ
                        хадгалах). */}
                    <Select
                      showSearch
                      allowClear
                      placeholder="Байгууллагын нэр"
                      optionFilterProp="label"
                      options={orgs.map((o) => ({
                        value: o.name,
                        label: o.name + (o.info ? ` · ${o.info}` : ""),
                      }))}
                    />
                  </Form.Item>
                  <Space align="start" style={{ width: "100%" }}>
                    <Form.Item
                      name="recipient_name"
                      label="Холбоо барих хүн"
                      extra="Хэнд хандаж байна"
                    >
                      <Input style={{ width: 200 }} maxLength={200} />
                    </Form.Item>
                    <Form.Item name="recipient_position" label="Албан тушаал">
                      <Input style={{ width: 220 }} maxLength={200} />
                    </Form.Item>
                  </Space>
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 10 }}
                    message="Гадны байгууллагад ТН байхгүй"
                    description={
                      "Тиймээс нэр, албан тушаалыг чөлөөт текстээр бичнэ. " +
                      "Хэвлэсэн бланкны дээд талд «<байгууллага> " +
                      "<тушаал> <нэр>-д» гэж гарна."
                    }
                  />
                </>
              )}

              {/* ⚠ АЛБАН ДУГААР — цуврал нь шатнаас автоматаар
                  тогтдог (лавлах: formal_req_rule.series_code), гэхдээ
                  СОЛИХ боломжтой: иж бүрэн хяналтын акт нь тусдаа
                  дэвтэртэй (№107/NN ИБ-ХАБ) бөгөөд гадны байгууллагад
                  явах бичиг өөр дэвтрээр дугаарлагдах тохиолдол бий. */}
              <Form.Item
                name="series_code"
                label="Албан бичгийн дэвтэр (цуврал)"
                extra={
                  seriesRow
                    ? `Анхдагч: ${seriesRow.name}${
                        seriesRow.next_sample
                          ? ` → ${seriesRow.next_sample}`
                          : " (дугаарлахгүй)"
                      }`
                    : "Шатны анхдагч цуврал лавлахад заагаагүй"
                }
              >
                <Select
                  allowClear
                  placeholder={
                    seriesRow ? `${seriesRow.name} (анхдагч)` : "Сонгох"
                  }
                  options={series.map((x) => ({
                    value: x.code,
                    label:
                      x.name +
                      (x.next_sample ? ` → ${x.next_sample}` : " (дугаарлахгүй)"),
                  }))}
                />
              </Form.Item>

              <Divider style={{ margin: "6px 0 10px" }}>
                <span style={{ fontSize: 11, color: MUTED }}>
                  «ИЙМД ШААРДАХ НЬ…»
                </span>
              </Divider>

              <Form.Item
                name="summary"
                label="Бланкны толгой догол мөр"
                extra={
                  rule?.intro_template
                    ? "Лавлахын загвараас шалгалтын өгөгдлөөр бөглөгдсөн — засаж болно."
                    : "Лавлахад загвар байхгүй. /settings/reference дээр нэмбэл дараа автоматаар бөглөгдөнө."
                }
              >
                <Input.TextArea rows={5} maxLength={4000} showCount />
              </Form.Item>

              <Space align="start" style={{ width: "100%" }}>
                <Form.Item
                  name="due_date"
                  label="Зөрчил арилгах хугацаа"
                  extra="Мөр бүр өөрийн хугацаатай бол тэр нь давуу"
                >
                  <DatePicker style={{ width: 170 }} />
                </Form.Item>
                <Form.Item
                  name="report_day"
                  label="Тайлан ирүүлэх өдөр"
                  extra="Сар бүрийн хэддүгээр өдөр"
                >
                  <Select
                    style={{ width: 130 }}
                    options={[5, 10, 15, 20, 25, 28].map((n) => ({
                      value: n,
                      label: `${n}-ны өдөр`,
                    }))}
                  />
                </Form.Item>
              </Space>

              {/* ⚠ Мөр бүрийн «шаардах арга хэмжээ» нь ЭНД бөглөгдөхгүй
                  гэдгийг ТОДОРХОЙ хэлнэ — эс бөгөөс ХАБЭАХ «бүгдийг
                  бөглөлөө» гэж бодоод, хоосон арга хэмжээтэй баримт
                  хэвлэнэ. Шалгалтын `fix_note` нь ХАРИУЦАГЧИЙН
                  биелэлтийн тэмдэглэл бөгөөд шаардах арга хэмжээ БИШ
                  тул автоматаар хуулж болохгүй. */}
              <Alert
                type="info"
                showIcon
                message="Зөрчил тус бүрийн «шаардах арга хэмжээ»-г дараа бөглөнө"
                description={
                  `${findingCount || 0} зөрчил мөр болж хуулагдана. Мөр ` +
                  "бүрийн шаардах арга хэмжээг албан шаардлагын хуудаснаас " +
                  "бөглөж, гарын үсэг цуглуулна."
                }
              />
            </Form>
          </>
        )}
      </Drawer>

      {/* ── ЖУРМЫН ЗААЛТ — шууд унших ─────────────────────────────── */}
      <Drawer
        open={!!clause}
        onClose={() => setClause(null)}
        width={600}
        title={
          clause
            ? `${clause.reg_code || "Журам"} · ${clause.clause}`
            : "Журмын заалт"
        }
      >
        {clause?.clause_text ? (
          <div
            style={{
              whiteSpace: "pre-wrap",
              fontSize: 13,
              lineHeight: 1.6,
              textAlign: "justify",
            }}
          >
            {clause.clause_text}
          </div>
        ) : (
          <Alert
            type="warning"
            showIcon
            message="Заалтын текст баазад олдсонгүй"
            description="Журам скан баримтаас OCR-оор уншигддаг тул текст дутуу байж болно. Эшлэл нь зөв."
          />
        )}
      </Drawer>
    </>
  );
}
